import React, { useEffect, useState } from 'react';
import { AccordionDetails, Grid, InputAdornment, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import moment from 'moment';
import { AccountData, DiscountOut, DiscountService, PassOut } from '../../../../api';
import { PassPaymentStatus } from '../../../../api/models/PassPaymentStatus';
import { Accordion, AccordionSummary } from '../../../../calendar/dialogs/EditOrderDialog/components/Accordion';
import { FormTextField } from '../../../../experienceSettings/formComponents/FormTextField';
import { FormDatePicker } from '../../../../experienceSettings/formComponents/FormDatePicker';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { getPassOrderPage } from '../../../../utils/AccountUtils';
import { getDateFormat } from '../../../../common/getDateAndTimeFormat';
import { getCurrencySymbol } from '../../../../utils/utils';
import TimeUtils from '../../../../utils/TimeUtils';
import { DiscountDetails } from '../../../../common/DiscountDetails';
import { getPassPaymentStatusIcon, getPassStatusIcon, isRecurringPassType } from '../../utils';
import { EditPassFormValues, PassDetailPanels } from '../../PassDetailTypes';
import { DetailRow } from './DetailRow';

type Props = {
  pass: PassOut;
  expandedPanel: PassDetailPanels | undefined;
  onPanelChange: (panel: PassDetailPanels) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
};

export const SummaryPanel = ({ pass, expandedPanel, onPanelChange }: Props) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { values, setFieldValue } = useFormikContext<EditPassFormValues>();
  const passOrderPage = getPassOrderPage(account, pass.order_page_id);
  const dateFormat = getDateFormat(account).longDateFormat;
  const currencySymbol = getCurrencySymbol(account);
  const createdAtLabel = TimeUtils.getLocalTime(pass.created_at, account.timezone).format(dateFormat);
  const [discount, setDiscount] = useState<DiscountOut | undefined>(undefined);
  // Recurring prices are owned by the pass order page - the API rejects a local override on edit_pass.
  const isRecurring = isRecurringPassType(passOrderPage?.pass_type);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!pass.discount_id) {
        setDiscount(undefined);
        return;
      }
      const result = await DiscountService.getDiscountById(pass.discount_id);
      if (!cancelled) setDiscount(result);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [pass.discount_id]);

  return (
    <Accordion
      expandedPanelName={expandedPanel}
      panelName={PassDetailPanels.Summary}
      onChange={onPanelChange}
      allOpenValue={PassDetailPanels.AllOpen}>
      <AccordionSummary
        title={t('passes.summary')}
        summaryComponent={
          <Grid container gap={1} alignItems="center">
            {getPassStatusIcon(pass, t, passOrderPage)}
            <Typography variant="body2" color="text.secondary">
              {pass.firstname} {pass.lastname}
            </Typography>
          </Grid>
        }
      />
      <AccordionDetails>
        <Grid container flexDirection="column" gap={1}>
          <DetailRow label={t('passes.pass_product')} value={passOrderPage?.label?.en || pass.order_page_id} />
          <DetailRow label={t('vouchers.createdat')} value={createdAtLabel} />
          <DetailRow label={t('vouchers.paymentStatus')} value={getPassPaymentStatusIcon(pass.payment_status, t)} />
          <DetailRow
            label={t('vouchers.status')}
            value={<Typography variant="body2">{t(`passes.status.${pass.status}`)}</Typography>}
          />
          {discount ? (
            <Grid container justifyContent="space-between" alignItems="center" flexWrap="nowrap" gap={1}>
              <Typography variant="body2" color="text.secondary">
                {t('passes.discount')}
              </Typography>
              <DiscountDetails discount={discount} initialPrice={pass.price} showLabel={false} />
            </Grid>
          ) : pass.discount_percentage ? (
            <DetailRow label={t('passes.discount')} value={`${pass.discount_percentage}%`} />
          ) : pass.discount_fixed_value ? (
            <DetailRow label={t('passes.discount')} value={`${pass.discount_fixed_value}${currencySymbol}`} />
          ) : null}
          <Grid container flexDirection="column" gap={1}>
            <Grid container alignItems="center" flexWrap="nowrap">
              <Grid item sx={{ width: 100, flexShrink: 0 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('passes.price')}
                </Typography>
              </Grid>
              <Grid item>
                <FormTextField
                  width={'137px'}
                  fieldName="price"
                  type="number"
                  endAdornment={<InputAdornment position="end">{currencySymbol}</InputAdornment>}
                  isFieldUpdate
                  disabled={isRecurring || pass.payment_status === PassPaymentStatus.PAID}
                />
              </Grid>
            </Grid>
            {isRecurring && (
              <Typography variant="caption" color="text.secondary">
                {t('passes.swap.price_managed_note')}
              </Typography>
            )}
            <Grid container alignItems="center" flexWrap="nowrap">
              <Grid item sx={{ width: 100, flexShrink: 0 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('passes.start_date')}
                </Typography>
              </Grid>
              <Grid item flex={1}>
                <FormDatePicker
                  fieldName="start_date"
                  isFieldUpdate
                  size="small"
                  fullWidth
                  onChange={(newValue) => {
                    if (!newValue || !values.start_date || !values.expiration_date) return;
                    const delta = newValue.diff(moment(values.start_date), 'days');
                    if (delta === 0) return;
                    void setFieldValue(
                      'expiration_date',
                      moment(values.expiration_date).add(delta, 'days').format('YYYY-MM-DD'),
                    );
                  }}
                />
              </Grid>
            </Grid>
            <Grid container alignItems="center" flexWrap="nowrap">
              <Grid item sx={{ width: 100, flexShrink: 0 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('passes.expiration')}
                </Typography>
              </Grid>
              <Grid item flex={1}>
                <FormDatePicker fieldName="expiration_date" isFieldUpdate size="small" fullWidth />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
